import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import { useGranjaAdmin } from '@/hooks/use-granja-admin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Plus, Skull } from 'lucide-react'
import { MortalidadeAvulsoForm } from '@/components/mortalidades/mortalidade-avulso-form'
import { MortalidadeList } from '@/components/mortalidades/mortalidade-list'
import { API_BASE } from '@/lib/api-base'
import {
  MORTALIDADE_STATUS,
  type Mortalidade,
  type MortalidadeCreatePayload,
  type MortalidadeListResponse,
  mortalidadeErrorMessage,
  mortalidadesQueryString,
  parseApiDetail,
} from '@/lib/mortalidades'

interface Galpao {
  id: string
  nome: string
  status: string
}

interface Lote {
  id: string
  codigo: string
  galpao_id: string
  status: string
}

type TabFilter = 'PENDENTE' | 'historico'

function MortalidadesPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  const { canDecideMortalidade } = useGranjaAdmin()
  const [galpoes, setGalpoes] = useState<Galpao[]>([])
  const [lotes, setLotes] = useState<Lote[]>([])
  const [items, setItems] = useState<Mortalidade[]>([])
  const [tab, setTab] = useState<TabFilter>('PENDENTE')
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(false)

  const fetchGalpoes = useCallback(async () => {
    const res = await apiFetch(`${apiBase}/api/galpoes/`)
    if (res.ok) {
      setGalpoes((await res.json()).filter((g: Galpao) => g.status === 'ativo'))
    }
  }, [apiBase, apiFetch])

  const fetchLotes = useCallback(async () => {
    const res = await apiFetch(`${apiBase}/api/lotes/`)
    if (res.ok) {
      setLotes((await res.json()).filter((l: Lote) => l.status === 'ativo'))
    }
  }, [apiBase, apiFetch])

  const fetchMortalidades = useCallback(async () => {
    setLoading(true)
    try {
      if (tab === 'PENDENTE') {
        const qs = mortalidadesQueryString({ status: MORTALIDADE_STATUS.PENDENTE, limit: 100 })
        const res = await apiFetch(`${apiBase}/api/mortalidades?${qs}`)
        if (res.ok) {
          const data = (await res.json()) as MortalidadeListResponse
          setItems(data.items)
        }
      } else {
        const res = await apiFetch(`${apiBase}/api/mortalidades?limit=100`)
        if (res.ok) {
          const data = (await res.json()) as MortalidadeListResponse
          setItems(
            data.items.filter(
              (m) =>
                m.status === MORTALIDADE_STATUS.CONFIRMADA ||
                m.status === MORTALIDADE_STATUS.REJEITADA,
            ),
          )
        }
      }
    } finally {
      setLoading(false)
    }
  }, [apiBase, apiFetch, tab])

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    fetchGalpoes()
    fetchLotes()
  }, [user, isSuperAdmin, selectedTenantId, fetchGalpoes, fetchLotes])

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    fetchMortalidades()
  }, [user, isSuperAdmin, selectedTenantId, fetchMortalidades])

  const handleCreate = async (payload: MortalidadeCreatePayload) => {
    const res = await apiFetch(`${apiBase}/api/mortalidades`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const detail = await parseApiDetail(res)
      throw new Error(mortalidadeErrorMessage(res.status, detail))
    }
    setShowForm(false)
    setTab(MORTALIDADE_STATUS.PENDENTE)
    await fetchMortalidades()
  }

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <div className="flex items-center gap-2">
          <Skull className="h-6 w-6 text-muted-foreground" />
          <h1 className="text-2xl font-bold">Mortalidades</h1>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" /> Lançamento avulso
        </Button>
      </div>

      <div className="flex gap-2">
        <Button
          variant={tab === 'PENDENTE' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTab('PENDENTE')}
        >
          Pendentes
        </Button>
        <Button
          variant={tab === 'historico' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTab('historico')}
        >
          Histórico
        </Button>
      </div>

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) setShowForm(false)
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">Lançamento avulso de mortalidade</DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">Lançamento avulso de mortalidade</h2>
            <MortalidadeAvulsoForm
              galpoes={galpoes}
              lotes={lotes}
              onCancel={() => setShowForm(false)}
              onSubmit={handleCreate}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>
            {tab === 'PENDENTE' ? 'Fila pendente de confirmação' : 'Confirmadas e rejeitadas'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : (
            <MortalidadeList
              items={items}
              galpoes={galpoes}
              lotes={lotes}
              canDecide={canDecideMortalidade}
              apiBase={apiBase}
              apiFetch={apiFetch}
              onChanged={fetchMortalidades}
              emptyMessage={
                tab === 'PENDENTE'
                  ? 'Nenhuma mortalidade pendente.'
                  : 'Nenhum registro no histórico.'
              }
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/mortalidades')({
  component: MortalidadesPage,
})
